import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { X, Upload, Image as ImageIcon, Loader2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SortableImage({ url, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get display URL (handle both relative and absolute URLs)
  const displayUrl = url.startsWith('/api/') 
    ? `${process.env.REACT_APP_BACKEND_URL}${url}`
    : url;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group aspect-square bg-zinc-800 rounded-sm overflow-hidden border border-zinc-700"
    >
      <img src={displayUrl} alt={`Product ${index + 1}`} className="w-full h-full object-cover" />
      
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 p-1 bg-black/70 rounded cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-4 w-4 text-white" />
      </button>
      
      {/* Remove button */}
      <button
        onClick={() => onRemove(index)}
        className="absolute top-2 right-2 p-1 bg-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
      >
        <X className="h-4 w-4 text-white" />
      </button>
      
      {/* Index badge */}
      <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/70 rounded text-xs text-white font-mono">
        {index + 1}
      </div>
    </div>
  );
}

export default function ImageUploader({ images = [], onChange, maxImages = 10 }) {
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDrop = useCallback(async (acceptedFiles) => {
    if (images.length + acceptedFiles.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    const newImages = [...images];

    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await axios.post(`${API}/upload/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newImages.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    onChange(newImages);
    setUploading(false);
  }, [images, onChange, maxImages]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: uploading || images.length >= maxImages
  });

  const handleRemove = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = images.indexOf(active.id);
      const newIndex = images.indexOf(over.id);
      onChange(arrayMove(images, oldIndex, newIndex));
    }
  };

  const handleUrlAdd = (e) => {
    e.preventDefault();
    const url = e.target.url.value.trim();
    if (!url) return;
    
    if (images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    onChange([...images, url]);
    e.target.reset();
  };

  return (
    <div className="space-y-4">
      {/* Existing Images */}
      {images.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={images} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((url, index) => (
                <SortableImage
                  key={url}
                  url={url}
                  index={index}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Drop Zone */}
      {images.length < maxImages && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-[#78BE20] bg-[#78BE20]/10'
              : 'border-zinc-700 hover:border-zinc-500'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="h-10 w-10 text-[#78BE20] animate-spin mb-2" />
              <p className="text-zinc-400">Uploading...</p>
            </div>
          ) : isDragActive ? (
            <div className="flex flex-col items-center">
              <Upload className="h-10 w-10 text-[#78BE20] mb-2" />
              <p className="text-[#78BE20]">Drop images here</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <ImageIcon className="h-10 w-10 text-zinc-500 mb-2" />
              <p className="text-zinc-400 mb-1">Drag & drop images here</p>
              <p className="text-zinc-500 text-sm">or click to browse</p>
              <p className="text-zinc-600 text-xs mt-2">
                Max {maxImages} images, 10MB each. JPG, PNG, GIF, WebP
              </p>
            </div>
          )}
        </div>
      )}

      {/* URL Input */}
      <form onSubmit={handleUrlAdd} className="flex gap-2">
        <input
          name="url"
          type="url"
          placeholder="Or paste image URL..."
          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-[#78BE20] focus:outline-none"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-sm transition-colors"
        >
          Add URL
        </button>
      </form>

      {/* Image count */}
      <p className="text-xs text-zinc-500">
        {images.length} / {maxImages} images
      </p>
    </div>
  );
}
